import { type FormEvent } from 'react';

// Типы для аналитики
interface FormAnalytics {
  formId: string;
  timeSpent: number;
  completionPercentage: number;
  fieldsFilled: string[];
  isComplete: boolean;
}

interface GtagEvent {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: unknown;
}

type YmFunction = ((id: number, action: string, ...args: unknown[]) => void) & {
  a: unknown[][];
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    ym: YmFunction;
  }
}

// Инициализация Google Analytics
export const initGA = (measurementId: string) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => {
    window.dataLayer.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);
};

// Инициализация Яндекс.Метрики
export const initYM = (counterId: number) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://mc.yandex.ru/metrika/tag.js`;
  document.head.appendChild(script);

  const ym: YmFunction = ((id: number, action: string, ...args: unknown[]) => {
    ym.a.push([id, action, ...args]);
  }) as YmFunction;
  ym.a = [];
  window.ym = ym;
  window.ym(counterId, 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true
  });
};

// Базовые методы аналитики
export const trackPageView = (path: string) => {
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  const ymId = Number(import.meta.env.VITE_YM_COUNTER_ID);

  if (gaId) {
    window.gtag('config', gaId, {
      page_path: path
    });
  }

  if (ymId) {
    window.ym(ymId, 'hit', path);
  }
};

export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  const ymId = Number(import.meta.env.VITE_YM_COUNTER_ID);

  if (gaId) {
    const eventData: GtagEvent = {
      event_category: category,
      event_label: label,
      value: value
    };
    window.gtag('event', action, eventData);
  }

  if (ymId) {
    window.ym(ymId, 'reachGoal', action, {
      category,
      label,
      value
    });
  }
};

// Специальные методы для форм
export const trackFormSubmission = (formId: string, data: Omit<FormAnalytics, 'formId'>) => {
  trackEvent('form', 'submit', formId);
  
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (gaId) {
    window.gtag('event', 'form_submit', {
      form_id: formId,
      time_spent: data.timeSpent,
      completion_percentage: data.completionPercentage,
      fields_filled: data.fieldsFilled.join(','),
      is_complete: data.isComplete
    });
  }
};

// Специальные методы для взаимодействия
export const trackButtonClick = (buttonName: string) => {
  trackEvent('interaction', 'button_click', buttonName);
};

export const trackEmailClick = (email: string) => {
  trackEvent('interaction', 'email_click', email);
};

export const trackPhoneClick = (phone: string) => {
  trackEvent('interaction', 'phone_click', phone);
};

// Специальные методы для каталога
export const trackSearch = (searchTerm: string) => {
  trackEvent('catalog', 'search', searchTerm);
};

export const trackProductClick = (productId: string, productName: string) => {
  trackEvent('catalog', 'product_click', productName, Number(productId));
};

// Хук для отслеживания форм
export const useTrackFormSubmission = (formName: string, data: Omit<FormAnalytics, 'formId'>) => {
  return (e: FormEvent) => {
    e.preventDefault();
    trackFormSubmission(formName, data);
  };
};